
import { ComparisonState } from '../types';

export const generateDynamicCode = (state: ComparisonState): string => {
  const { 
    beforeImage, afterImage, title, beforeLabel, afterLabel, beforeDesc, afterDesc,
    orientation, splitPosition, separatorColor, separatorWidth, 
    beforeLabelPos, afterLabelPos, beforeLabelSize, afterLabelSize,
    fontFamily, fontSize, accentColor, bgColor, borderRadius, padding,
    labelColor, descColor
  } = state;

  const isHorizontal = orientation === 'horizontal';
  const fontsImport = `https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&family=Roboto+Mono&family=Unbounded:wght@400;900&family=Space+Grotesk:wght@300;700&display=swap`;

  return `<!DOCTYPE html>
<!-- 
  CONFIGURATION DES IMAGES 
  Modifiez les URLs ci-dessous pour mettre à jour vos visuels :
-->
<script>
  window.PERSPECTIVE_CONFIG = {
    beforeImageUrl: "${beforeImage}",
    afterImageUrl: "${afterImage}"
  };
</script>

<div id="perspective-wrapper" class="perspective-container">
  ${title ? `<h2 class="perspective-title">${title}</h2>` : ''}
  
  <div class="perspective-viewer" style="--split: ${splitPosition}%;">
    <div class="perspective-images">
      <div class="image-box before">
        <img id="p-img-before" src="${beforeImage}" alt="Before">
      </div>
      <div class="image-box after">
        <img id="p-img-after" src="${afterImage}" alt="After">
      </div>
    </div>

    <input type="range" min="0" max="100" value="${splitPosition}" class="perspective-slider" id="p-slider">
    <div class="perspective-handle" id="p-handle"></div>

    <!-- Labels -->
    <div class="p-label p-label-before" style="left: ${beforeLabelPos.x}%; top: ${beforeLabelPos.y}%; width: ${beforeLabelSize.width}px; height: ${beforeLabelSize.height}px;">
      <div class="p-label-content">
        <h4>${beforeLabel}</h4>
        ${beforeDesc ? `<p>${beforeDesc}</p>` : ''}
      </div>
    </div>

    <div class="p-label p-label-after" style="left: ${afterLabelPos.x}%; top: ${afterLabelPos.y}%; width: ${afterLabelSize.width}px; height: ${afterLabelSize.height}px;">
      <div class="p-label-content">
        <h4>${afterLabel}</h4>
        ${afterDesc ? `<p>${afterDesc}</p>` : ''}
      </div>
    </div>
  </div>
</div>

<style>
  @import url('${fontsImport}');

  .perspective-container {
    --bg: ${bgColor};
    --accent: ${accentColor};
    --sep-color: ${separatorColor};
    --sep-width: ${separatorWidth}px;
    --font: '${fontFamily}', sans-serif;
    --label-color: ${labelColor};
    --desc-color: ${descColor};
    --radius: ${borderRadius}px;
    --padding-internal: ${padding}px;
    
    background: var(--bg);
    padding: 80px 80px; /* Padding externe généreux pour faire respirer les bordures */
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: var(--font);
    overflow: hidden;
  }

  .perspective-title {
    color: var(--accent);
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-weight: 900;
    margin-bottom: 48px;
    font-size: ${fontSize * 1.5}px;
    text-align: center;
    width: 100%;
  }

  .perspective-viewer {
    position: relative;
    width: 100%;
    max-width: 900px;
    height: 600px;
    background: #000;
    border-radius: var(--radius);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    overflow: hidden;
  }

  .perspective-images { position: absolute; inset: 0; }

  .image-box {
    position: absolute;
    inset: 0;
    padding: var(--padding-internal);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--radius);
    display: block;
  }

  .image-box.after {
    ${isHorizontal ? 'clip-path: inset(0 calc(100% - var(--split)) 0 0);' : 'clip-path: inset(0 0 calc(100% - var(--split)) 0);'}
    z-index: 2;
  }

  .perspective-slider {
    position: absolute;
    ${isHorizontal ? 'inset: 0;' : 'inset: 0; transform: rotate(90deg);'}
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: 10;
    cursor: ${isHorizontal ? 'ew-resize' : 'ns-resize'};
    margin: 0;
  }

  .perspective-handle {
    position: absolute;
    z-index: 5;
    background: var(--sep-color);
    pointer-events: none;
    ${isHorizontal ? `
      left: var(--split);
      top: 0;
      bottom: 0;
      width: var(--sep-width);
      transform: translateX(-50%);
    ` : `
      top: var(--split);
      left: 0;
      right: 0;
      height: var(--sep-width);
      transform: translateY(-50%);
    `}
  }

  .perspective-handle::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 44px;
    height: 44px;
    background: #fff;
    border: 3px solid var(--sep-color);
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  .p-label {
    position: absolute;
    z-index: 20;
    pointer-events: none;
    transform: translate(-50%, -50%);
  }

  .p-label-content {
    background: rgba(15, 23, 42, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 16px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .p-label h4 {
    margin: 0 0 4px 0;
    text-transform: uppercase;
    font-weight: 900;
    font-size: ${fontSize}px;
    color: var(--label-color);
  }

  .p-label p {
    margin: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--desc-color);
  }
</style>

<script>
  (function() {
    const slider = document.getElementById('p-slider');
    const viewer = slider.parentElement;
    const config = window.PERSPECTIVE_CONFIG || {};

    if (config.beforeImageUrl) document.getElementById('p-img-before').src = config.beforeImageUrl;
    if (config.afterImageUrl) document.getElementById('p-img-after').src = config.afterImageUrl;
    
    slider.addEventListener('input', (e) => {
      viewer.style.setProperty('--split', e.target.value + '%');
    });
  })();
</script>
`;
};
